/**
 * Cliente HTTP tipado.
 *
 * Todas las peticiones van a `/api`, que en desarrollo proxea Vite hacia la API y en
 * producción proxea nginx. Así el frontend nunca conoce la URL del backend y no hay ninguna
 * variable de entorno de build que pueda quedarse mal.
 */
import type {
  AlertsResponse,
  CadastralSuggestion,
  ChargePreview,
  AgreementReadingResponse,
  ContractReadingResponse,
  GeocodeResponse,
  LeaseChainEntry,
  LeaseSummary,
  PropertyMapResponse,
  PropertyPhotoDto,
  RenewalPreview,
  TenantDetailResponse,
  TenantDocumentCategory,
  TenantDocumentUploadResponse,
  TenantPurgePreview,
  IdDocumentReadingResponse,
  TenantNoteDto,
  TerminationPreview,
  TimelineEntry,
  UnitDto,
  ApiErrorBody,
  DashboardAnalyticsResponse,
  DashboardPerformanceResponse,
  DashboardSummaryResponse,
  DocumentListRow,
  HealthResponse,
  MaintenanceIssuesResponse,
  Paginated,
  PortalMeResponse,
  LedgerMonthResponse,
  LedgerUploadResult,
  BankMovementActionInput,
  AiReconciliationRun,
  RememberPreview,
  RememberPreviewInput,
  StatementImportResult,
  PortfolioSummary,
  PropertiesResponse,
  PropertyDetailResponse,
  RentChargesResponse,
  ReportCatalogItem,
  TenantCard,
  TransactionsResponse,
  PendingResponse,
  AssistantChatMessage,
  AssistantConversation,
  AssistantConversationSummary,
  AssistantReply,
  AssistantStoredMessage,
  AssistantStatus,
  UtilitiesResponse,
  RotationResponse,
} from '@naar/shared';

import { clearSession, getAccessToken, isAccessTokenExpired, refreshSession } from './session';

const BASE = '/api';

/** Error de API con el código del backend, para que la UI pueda distinguir casos. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * Carga útil del error de negocio. Es lo que permite pintar el mensaje EN EL CAMPO
   * correcto y ofrecer la acción siguiente: un 409 de catastral duplicada trae el
   * identificador del inmueble que ya la tiene, para poder enlazarlo.
   */
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Errores de validación con el formato `[{ path, message }]`. */
  get fieldErrors(): { path: string; message: string }[] {
    return Array.isArray(this.details)
      ? (this.details as { path: string; message: string }[]).filter(
          (detail) => typeof detail?.path === 'string',
        )
      : [];
  }
}

type QueryValue = string | number | boolean | undefined | null;

function buildUrl(path: string, params?: Record<string, QueryValue>): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return `${url.pathname}${url.search}`;
}

/**
 * Tiempo máximo por petición. Sin él, una API colgada (no caída: colgada) deja la pantalla en
 * el esqueleto de carga indefinidamente, que es el peor de los estados posibles: parece que
 * funciona y no va a terminar nunca.
 */
const REQUEST_TIMEOUT_MS = 12_000;

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
  params?: Record<string, QueryValue>;
  body?: unknown;
  /** `updatedAt` del recurso, para el bloqueo optimista. Viaja como `If-Unmodified-Since`. */
  version?: string;
  /** El cuerpo ya es un `FormData` (subida de ficheros): no se serializa ni se fija el tipo. */
  formData?: FormData;
  /** Plazo propio para peticiones que esperan a un servicio externo (el Asistente). */
  timeoutMs?: number;
}

async function send(path: string, method: Method, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.version) headers['If-Unmodified-Since'] = options.version;
  if (options.body !== undefined && !options.formData) headers['Content-Type'] = 'application/json';

  return fetch(buildUrl(path, options.params), {
    method,
    headers,
    // La cookie de refresco es `SameSite=Strict` y solo se manda a `/api/auth`, pero hace
    // falta declarar las credenciales para que el navegador la incluya siquiera.
    credentials: 'same-origin',
    signal: AbortSignal.timeout(options.timeoutMs ?? REQUEST_TIMEOUT_MS),
    ...(options.formData
      ? { body: options.formData }
      : options.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
  });
}

async function request<T>(path: string, method: Method = 'GET', options: RequestOptions = {}): Promise<T> {
  /**
   * Renovación PREVENTIVA: si el token ya ha caducado (o está a punto), se renueva antes de
   * salir. Evita el 401 y el reintento en el caso más común, que es volver a una pestaña
   * abierta desde hace veinte minutos.
   */
  if (isAccessTokenExpired() && !path.startsWith('/auth/')) {
    await refreshSession();
  }

  let response: Response;
  try {
    response = await send(path, method, options);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new ApiError(
        0,
        'TIMEOUT',
        `La API no ha respondido en ${(options.timeoutMs ?? REQUEST_TIMEOUT_MS) / 1000} segundos.`,
      );
    }
    // Fallo de red: la API no responde. Se traduce a un error con código propio para que la
    // pantalla pueda decir «no se ha podido conectar» en lugar de un mensaje genérico.
    throw new ApiError(0, 'NETWORK_ERROR', 'No se ha podido conectar con la API.');
  }

  /**
   * 401 con un token que creíamos bueno: la sesión caducó entre medias. Se renueva UNA vez y
   * se reintenta. Si el refresco tampoco vale, se limpia la sesión y la app enseña el login.
   */
  if (response.status === 401 && !path.startsWith('/auth/')) {
    const renewed = await refreshSession();
    if (renewed) {
      response = await send(path, method, options);
    } else {
      clearSession();
    }
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let code = 'HTTP_ERROR';
    let message = `La API ha respondido ${response.status}.`;
    let details: unknown;
    try {
      const body = (await response.json()) as ApiErrorBody;
      if (body?.error) {
        code = body.error.code;
        message = body.error.message;
        details = body.error.details;
      }
    } catch {
      // El cuerpo no era JSON: se conserva el mensaje genérico.
    }
    throw new ApiError(response.status, code, message, details);
  }

  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// Endpoints. Un método por ruta, con el tipo de `@naar/shared`.
// ---------------------------------------------------------------------------

export const api = {
  health: () => request<HealthResponse>('/health', 'GET'),

  dashboardSummary: () => request<DashboardSummaryResponse>('/dashboard/summary', 'GET'),
  dashboardPerformance: () => request<DashboardPerformanceResponse>('/dashboard/performance', 'GET'),
  dashboardAnalytics: () => request<DashboardAnalyticsResponse>('/dashboard/analytics', 'GET'),

  portfolios: () => request<{ data: PortfolioSummary[] }>('/portfolios', 'GET'),

  properties: (params?: {
    q?: string;
    portfolio?: string;
    city?: string;
    status?: string;
    minValue?: string;
    maxValue?: string;
    sort?: string;
    dir?: string;
    page?: number;
    limit?: number;
  }) => request<PropertiesResponse>('/properties', 'GET', { params }),

  propertyMap: (params?: { portfolio?: string; status?: string }) =>
    request<PropertyMapResponse>('/properties/map', 'GET', { params }),

  property: (id: string) => request<PropertyDetailResponse>(`/properties/${id}`, 'GET'),

  tenants: (params?: {
    status?: string;
    property?: string;
    /** Todos: también los que no tienen contrato activo y los dados de baja. */
    historic?: boolean;
    page?: number;
    limit?: number;
  }) =>
    request<Paginated<TenantCard>>('/tenants', 'GET', {
      params: {
        ...params,
        historic: params?.historic ? 'true' : undefined,
      },
    }),

  transactions: (params?: {
    property?: string;
    category?: string;
    review_status?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }) => request<TransactionsResponse>('/transactions', 'GET', { params }),

  rentCharges: (params?: { month?: string }) =>
    request<RentChargesResponse>('/rent-charges', 'GET', { params }),

  maintenanceIssues: (params?: { property?: string; priority?: string }) =>
    request<MaintenanceIssuesResponse>('/maintenance/issues', 'GET', { params }),

  documents: (params?: { category?: string; property?: string; page?: number; limit?: number }) =>
    request<Paginated<DocumentListRow>>('/documents', 'GET', { params }),

  /** Suministros de un inmueble en un año, con la regularización de cada cuatrimestre. */
  utilities: (params: { propertyId?: string; year?: number }) =>
    request<UtilitiesResponse>('/utilities', 'GET', { params }),
  /** Corrige los días de estancia de un contrato en un cuatrimestre; `days: null` vuelve a los del contrato. */
  /** Rotación anual de inquilinos por piso: salidas ÷ habitaciones, entradas y estancia media. */
  rotation: (year?: number) =>
    request<RotationResponse>('/properties/rotation', 'GET', { params: year ? { year } : {} }),
  setUtilityStay: (body: { leaseId: string; year: number; period: number; days: number | null }) =>
    request<{ leaseId: string; days: number | null }>('/utilities/stay', 'PUT', { body }),
  /** Lo entregado para suministros en un mes, corregido a mano (`amount: null` vuelve a la conciliación). */
  setUtilityDelivered: (body: { leaseId: string; month: string; amount: string | null }) =>
    request<{ leaseId: string; month: string; amount: string | null }>('/utilities/delivered', 'PUT', { body }),
  /** Pendientes: lo que hay que subir o hacer, calculado al vuelo. Incluye las alertas. */
  pending: () => request<PendingResponse>('/pending', 'GET'),
  assistantStatus: () => request<AssistantStatus>('/assistant/status', 'GET'),
  /** Puede tardar: el Asistente consulta varias cosas y espera a OpenAI entre medias. */
  assistantChat: (messages: AssistantChatMessage[]) =>
    request<AssistantReply>('/assistant/chat', 'POST', { body: { messages }, timeoutMs: 65_000 }),
  /** Historial del Asistente: solo las conversaciones de quien está dentro. */
  assistantConversations: () =>
    request<{ data: AssistantConversationSummary[] }>('/assistant/conversations', 'GET'),
  assistantConversation: (id: string) => request<AssistantConversation>(`/assistant/conversations/${id}`, 'GET'),
  saveAssistantConversation: (id: string, messages: AssistantStoredMessage[]) =>
    request<AssistantConversationSummary>(`/assistant/conversations/${id}`, 'PUT', { body: { messages } }),
  deleteAssistantConversation: (id: string) => request<void>(`/assistant/conversations/${id}`, 'DELETE'),
  /** Descarta las alertas de una tarea (sobre todo oportunidades). */
  dismissPending: (alertIds: string[]) =>
    request<{ dismissed: number }>('/pending/dismiss', 'POST', { body: { alertIds } }),
  alerts: (params?: { status?: string; severity?: string }) =>
    request<AlertsResponse>('/alerts', 'GET', { params }),

  reports: () => request<{ data: ReportCatalogItem[] }>('/reports', 'GET'),

  portalMe: () => request<PortalMeResponse>('/portal/me', 'GET'),

  // -------------------------------------------------------------------------
  // Fase 2 - escritura
  //
  // `version` viaja como `If-Unmodified-Since` y es el bloqueo optimista: sin el, dos
  // gestores editando el mismo inmueble se pisan en silencio.
  // -------------------------------------------------------------------------

  createPortfolio: (body: unknown) => request<PortfolioSummary>('/portfolios', 'POST', { body }),
  updatePortfolio: (id: string, body: unknown) =>
    request<PortfolioSummary>(`/portfolios/${id}`, 'PATCH', { body }),
  deletePortfolio: (id: string) => request<void>(`/portfolios/${id}`, 'DELETE'),

  createProperty: (body: unknown) => request<PropertyWriteResponse>('/properties', 'POST', { body }),
  updateProperty: (id: string, body: unknown, version?: string) =>
    request<PropertyWriteResponse>(`/properties/${id}`, 'PATCH', {
      body,
      ...(version ? { version } : {}),
    }),
  deleteProperty: (id: string) => request<void>(`/properties/${id}`, 'DELETE'),
  restoreProperty: (id: string) => request<void>(`/properties/${id}/restore`, 'POST'),
  changePropertyStatus: (id: string, body: unknown) =>
    request<void>(`/properties/${id}/status`, 'POST', { body }),

  createUnit: (propertyId: string, body: unknown) =>
    request<UnitWriteResponse>(`/properties/${propertyId}/units`, 'POST', { body }),
  updateUnit: (id: string, body: unknown, version?: string) =>
    request<UnitWriteResponse>(`/units/${id}`, 'PATCH', { body, ...(version ? { version } : {}) }),
  deleteUnit: (id: string) => request<void>(`/units/${id}`, 'DELETE'),
  availableUnits: (params?: { property?: string; includeOccupied?: boolean }) =>
    request<{ data: AvailableUnit[] }>('/units/available', 'GET', {
      params: {
        property: params?.property,
        includeOccupied: params?.includeOccupied ? 'true' : undefined,
      },
    }),

  createValuation: (propertyId: string, body: unknown) =>
    request<{ id: string; marketValue: string | null }>(
      `/properties/${propertyId}/valuations`,
      'POST',
      { body },
    ),
  deleteValuation: (id: string) =>
    request<{ marketValue: string | null }>(`/valuations/${id}`, 'DELETE'),

  createLoan: (propertyId: string, body: unknown) =>
    request<unknown>(`/properties/${propertyId}/loans`, 'POST', { body }),
  updateLoan: (id: string, body: unknown) => request<unknown>(`/loans/${id}`, 'PATCH', { body }),
  deleteLoan: (id: string) => request<void>(`/loans/${id}`, 'DELETE'),

  createInsurance: (propertyId: string, body: unknown) =>
    request<unknown>(`/properties/${propertyId}/insurance`, 'POST', { body }),
  updateInsurance: (id: string, body: unknown) =>
    request<unknown>(`/insurance/${id}`, 'PATCH', { body }),
  deleteInsurance: (id: string) => request<void>(`/insurance/${id}`, 'DELETE'),

  createExpense: (propertyId: string, body: unknown) =>
    request<unknown>(`/properties/${propertyId}/expenses`, 'POST', { body }),
  updateExpense: (id: string, body: unknown) => request<unknown>(`/expenses/${id}`, 'PATCH', { body }),
  deleteExpense: (id: string) => request<void>(`/expenses/${id}`, 'DELETE'),

  updatePhoto: (id: string, body: unknown) =>
    request<PropertyPhotoDto>(`/photos/${id}`, 'PATCH', { body }),
  reorderPhotos: (propertyId: string, order: string[]) =>
    request<{ reordered: number }>(`/properties/${propertyId}/photos/order`, 'PUT', {
      body: { order },
    }),
  deletePhoto: (id: string) => request<void>(`/photos/${id}`, 'DELETE'),

  // --- Inquilinos ----------------------------------------------------------

  tenant: (id: string) => request<TenantDetailResponse>(`/tenants/${id}`, 'GET'),
  tenantTimeline: (id: string) =>
    request<{ entries: TimelineEntry[] }>(`/tenants/${id}/timeline`, 'GET'),
  createTenant: (body: unknown) => request<TenantWriteResponse>('/tenants', 'POST', { body }),
  /** Lee el PDF del contrato y devuelve con qué rellenar el alta. No crea ni guarda nada. */
  readTenantContract: (file: File) =>
    uploadWithProgress<ContractReadingResponse>('/tenants/contract-reading', file),
  /** Lee una hoja de renovación o de rescisión para el alta. No crea ni guarda nada. */
  readTenantAgreement: (file: File) =>
    uploadWithProgress<AgreementReadingResponse>('/tenants/agreement-reading', file),
  /**
   * Un papel del inquilino en su sitio de la ficha. El último subido de cada categoría manda. La
   * renovación y la rescisión, además, se leen y cambian el contrato (`leaseChange`). Con un
   * contrato, `iban` es la cuenta que se ha leído de él si la ficha no tenía ninguna.
   */
  uploadTenantDocument: (
    tenantId: string,
    category: TenantDocumentCategory,
    file: File,
    /** `ocr: false` si el DNI o el contrato ya se han leído al rellenar el formulario. */
    options: { ocr?: boolean } = {},
  ) =>
    uploadWithProgress<TenantDocumentUploadResponse>(
      `/tenants/${tenantId}/documents`,
      file,
      undefined,
      { category, ...(options.ocr === false ? { ocr: 'false' } : {}) },
    ),
  /** OCR local del DNI, NIE o pasaporte: la fecha de nacimiento. No guarda nada. */
  readTenantIdDocument: (file: File) =>
    uploadWithProgress<IdDocumentReadingResponse>('/tenants/id-reading', file),
  /** Borrar la hoja de renovación la deshace; la de una rescisión pendiente, la cancela. */
  deleteTenantDocument: (tenantId: string, documentId: string) =>
    request<void>(`/tenants/${tenantId}/documents/${documentId}`, 'DELETE'),
  updateTenant: (id: string, body: unknown, version?: string) =>
    request<TenantWriteResponse>(`/tenants/${id}`, 'PATCH', {
      body,
      ...(version ? { version } : {}),
    }),
  deleteTenant: (id: string) => request<void>(`/tenants/${id}`, 'DELETE'),
  restoreTenant: (id: string) => request<void>(`/tenants/${id}/restore`, 'POST'),
  /**
   * Derecho de supresión del RGPD. NO es un borrado: sustituye los datos personales y conserva
   * contratos y pagos, que respaldan ejercicios ya declarados. Es irreversible.
   */
  anonymizeTenant: (id: string) => request<void>(`/tenants/${id}/anonymize`, 'POST'),
  /** Qué se llevaría por delante borrar a un inquilino con todo lo suyo. No toca nada. */
  tenantPurgePreview: (id: string) =>
    request<TenantPurgePreview>(`/tenants/${id}/purge-preview`, 'GET'),
  /** Irreversible: contratos, cargos, cobros y documentos. `confirmation` es su nombre. */
  purgeTenant: (id: string, confirmation: string) =>
    request<TenantPurgePreview>(`/tenants/${id}/purge`, 'POST', { body: { confirmation } }),

  createTenantNote: (tenantId: string, body: unknown) =>
    request<TenantNoteDto>(`/tenants/${tenantId}/notes`, 'POST', { body }),
  updateTenantNote: (id: string, body: unknown) =>
    request<TenantNoteDto>(`/tenant-notes/${id}`, 'PATCH', { body }),
  deleteTenantNote: (id: string) => request<void>(`/tenant-notes/${id}`, 'DELETE'),

  invitePortal: (tenantId: string, body?: unknown) =>
    request<{ inviteUrl: string; inviteToken: string; email: string }>(
      `/tenants/${tenantId}/portal-invite`,
      'POST',
      { body: body ?? {} },
    ),
  revokePortal: (tenantId: string) =>
    request<void>(`/tenants/${tenantId}/portal-access`, 'DELETE'),

  // --- Contratos -----------------------------------------------------------

  lease: (id: string) => request<LeaseSummary>(`/leases/${id}`, 'GET'),
  leaseChain: (id: string) => request<{ chain: LeaseChainEntry[] }>(`/leases/${id}/chain`, 'GET'),
  createLease: (body: unknown) => request<LeaseSummary>('/leases', 'POST', { body }),
  /** Cambia de habitación —o de inmueble— un contrato en borrador o activo. */
  moveLeaseUnit: (id: string, unitId: string, version?: string) =>
    request<LeaseSummary>(`/leases/${id}/move-unit`, 'POST', {
      body: { unitId },
      ...(version ? { version } : {}),
    }),
  updateLease: (id: string, body: unknown, version?: string) =>
    request<LeaseSummary>(`/leases/${id}`, 'PATCH', { body, ...(version ? { version } : {}) }),
  chargePreview: (id: string) =>
    request<{ charges: ChargePreview[]; total: string }>(`/leases/${id}/charge-preview`, 'GET'),
  activateLease: (id: string, body?: unknown) =>
    request<LeaseSummary & { chargesCreated: number }>(`/leases/${id}/activate`, 'POST', {
      body: body ?? {},
    }),
  previewRenewal: (id: string) => request<RenewalPreview>(`/leases/${id}/preview-renewal`, 'GET'),
  renewLease: (id: string, body: unknown) =>
    request<LeaseSummary>(`/leases/${id}/renew`, 'POST', { body }),
  previewTermination: (id: string, effectiveAt: string) =>
    request<TerminationPreview>(`/leases/${id}/preview-termination`, 'GET', {
      params: { effectiveAt },
    }),
  terminateLease: (id: string, body: unknown) =>
    request<LeaseSummary & { impact: TerminationPreview }>(`/leases/${id}/terminate`, 'POST', {
      body,
    }),
  updateDeposit: (id: string, body: unknown) =>
    request<LeaseSummary>(`/leases/${id}/deposit`, 'PATCH', { body }),
  addLeaseTenant: (id: string, body: unknown) =>
    request<LeaseSummary>(`/leases/${id}/tenants`, 'POST', { body }),
  removeLeaseTenant: (id: string, tenantId: string, body?: unknown) =>
    request<LeaseSummary>(`/leases/${id}/tenants/${tenantId}`, 'DELETE', { body: body ?? {} }),

  // --- Ficheros y geocodificacion ------------------------------------------

  uploadPhoto: (propertyId: string, file: File, onProgress?: (percent: number) => void) =>
    uploadWithProgress<PropertyPhotoDto>(`/properties/${propertyId}/photos`, file, onProgress),

  /** Descarga un documento. Por qué con `fetch` y no con un enlace: ver `downloadFile`. */
  downloadDocument: (id: string, fallbackName: string): Promise<void> =>
    downloadFile(`/documents/${id}/download`, fallbackName, 'No se ha podido descargar el documento.'),
  /** El documento en memoria, para el visor: no se guarda en el disco. El acceso queda registrado igual. */
  fetchDocument: (id: string, fallbackName: string) =>
    fetchFile(`/documents/${id}/download`, fallbackName, 'No se ha podido abrir el documento.'),
  /** Primera página de un PDF como imagen (el plano del piso en el resumen). */
  documentPreview: (id: string) =>
    fetchFile(`/documents/${id}/preview`, 'vista-previa.png', 'No se ha podido generar la vista previa.'),

  propertyDeeds: (propertyId: string) =>
    request<{ data: PropertyDeedDto[] }>(`/properties/${propertyId}/deeds`, 'GET'),
  /** El fichero es opcional: se puede registrar la finca y adjuntar el PDF más tarde. */
  uploadDeed: (
    propertyId: string,
    file: File | null,
    fields: Record<string, string | null | undefined>,
    onProgress?: (percent: number) => void,
  ) =>
    uploadWithProgress<PropertyDeedDto>(
      `/properties/${propertyId}/deeds`,
      file,
      onProgress,
      fields,
    ),
  deleteDeed: (id: string) => request<void>(`/deeds/${id}`, 'DELETE'),

  // -------------------------------------------------------------------------
  // Ingresos y gastos
  // -------------------------------------------------------------------------

  ledgerMonth: (month: string) => request<LedgerMonthResponse>(`/ledger/${month}`, 'GET'),
  /** `reopened`: el mes estaba conciliado y, al añadir el gasto, ha pasado a incompleto. */
  createLedgerEntry: (month: string, body: unknown) =>
    request<{ id: string; reopened: boolean }>(`/ledger/${month}/entries`, 'POST', { body }),
  updateLedgerEntry: (id: string, body: unknown) =>
    request<void>(`/ledger/entries/${id}`, 'PATCH', { body }),
  deleteLedgerEntry: (id: string) => request<void>(`/ledger/entries/${id}`, 'DELETE'),
  reconcileMonth: (month: string, body: unknown) =>
    request<{ status: 'conciliado' | 'incompleto'; gapCount: number }>(
      `/ledger/${month}/reconcile`,
      'POST',
      { body },
    ),
  reopenMonth: (month: string) => request<void>(`/ledger/${month}/reopen`, 'POST'),
  /**
   * Suelta una factura y deja que el servidor adivine qué es. `attached` dice si se ha
   * adjuntado a un gasto que ya estaba apuntado esperando su ticket, en vez de crear otro.
   */
  /**
   * Soltar un ticket en un inmueble SIN decir el mes: lo decide la fecha que se lee en él.
   * Devuelve `month`, el mes en que ha quedado apuntado.
   */
  uploadTicket: (propertyId: string | null, file: File, onProgress?: (percent: number) => void) =>
    uploadWithProgress<LedgerUploadResult>(
      '/ledger/uploads',
      file,
      onProgress,
      // Sin piso, la API lo saca del nombre del fichero o de la factura (o responde PROPERTY_UNKNOWN).
      { propertyId },
    ),
  uploadLedgerFile: (
    month: string,
    propertyId: string,
    file: File,
    onProgress?: (percent: number) => void,
    /** Soltado en el hueco de un gasto fijo que «no ha llegado»: el gasto es ese. */
    recurringExpenseId?: string,
  ) =>
    uploadWithProgress<{ id: string; attached: boolean; reopened: boolean }>(`/ledger/${month}/uploads`, file, onProgress, {
      propertyId,
      recurringExpenseId,
    }),
  /** Cuelga la factura de un gasto que ya estaba apuntado sin ella. */
  attachEntryDocument: (entryId: string, file: File) =>
    uploadWithProgress<{ id: string; reopened: boolean }>(`/ledger/entries/${entryId}/document`, file),
  /** Cambia la factura de un gasto por otra (la anterior sigue en Documentos). */
  replaceEntryDocument: (entryId: string, file: File) =>
    uploadWithProgress<{ id: string; reopened: boolean }>(`/ledger/entries/${entryId}/document`, file, undefined, {
      reemplazar: '1',
    }),
  /** Proveedor, observaciones o inmueble de un movimiento del banco, desde la tabla. */
  updateMovementDetails: (
    id: string,
    body: {
      vendorName?: string | null;
      notes?: string | null;
      propertyId?: string;
      /** La habitación de un cargo, en su inmueble; `null` la quita. */
      unitId?: string | null;
      /** Un cargo de varios pisos: se reparte a partes iguales entre ellos. */
      propertyIds?: string[];
      /** Quién paga un ingreso. */
      tenantId?: string;
      /** Ponerlo también en los ingresos exactamente iguales (concepto e importe). */
      applyToSame?: boolean;
      /** Aplicar lo que propone la IA (su duda o su aviso), tal cual. */
      acceptAiProposal?: boolean;
    },
  ) => request<{ alsoChanged: number }>(`/ledger/movements/${id}/details`, 'PATCH', { body }),
  attachMovementDocument: (id: string, file: File) =>
    uploadWithProgress<{ id: string; reopened: boolean }>(`/ledger/movements/${id}/document`, file),
  removeMovementDocument: (id: string) => request<void>(`/ledger/movements/${id}/document`, 'DELETE'),
  updatePaymentNotes: (id: string, reference: string | null) =>
    request<void>(`/ledger/payments/${id}`, 'PATCH', { body: { reference } }),
  attachPaymentDocument: (id: string, file: File) =>
    uploadWithProgress<{ id: string; reopened: boolean }>(`/ledger/payments/${id}/document`, file),
  removePaymentDocument: (id: string) => request<void>(`/ledger/payments/${id}/document`, 'DELETE'),
  /** El ticket de un cargo del banco: crea su gasto en el inmueble, ya enlazado al cargo. */
  attachMovementTicket: (movementId: string, file: File, fields: { propertyId?: string; concept?: string }) =>
    uploadWithProgress<{ id: string; property: { id: string; name: string } }>(
      `/ledger/movements/${movementId}/ticket`,
      file,
      undefined,
      fields,
    ),
  /** Una renta cobrada fuera del banco (efectivo, Bizum…), con su justificante si lo hay. */
  registerOffBankPayment: (
    chargeId: string,
    fields: { method: string; amount: string; paidAt: string },
    file: File | null,
  ) =>
    uploadWithProgress<{ id: string; reopened: boolean }>(`/ledger/charges/${chargeId}/payments`, file, undefined, fields),
  deleteOffBankPayment: (id: string) => request<void>(`/ledger/payments/${id}`, 'DELETE'),
  /** Tickets y justificantes de `from` a `to` en un ZIP, por carpetas de mes. */
  downloadLedgerExport: (from: string, to: string): Promise<void> =>
    downloadFile(
      `/ledger/export?from=${from}&to=${to}`,
      from === to ? `ingresos-y-gastos-${from}.zip` : `ingresos-y-gastos-${from}_${to}.zip`,
      'No se ha podido preparar la descarga.',
    ),
  /** Sube el extracto del banco. Cada movimiento cae en el mes de su fecha. */
  uploadStatement: (file: File, onProgress?: (percent: number) => void) =>
    uploadWithProgress<StatementImportResult>('/ledger/statements', file, onProgress),
  deleteStatement: (id: string) => request<void>(`/ledger/statements/${id}`, 'DELETE'),
  /** Devuelve cuántos movimientos de otros meses ha cambiado de paso (al recordar con `applyTo`). */
  resolveMovement: (id: string, body: BankMovementActionInput) =>
    request<{ alsoChanged: number }>(`/ledger/movements/${id}`, 'PATCH', { body }),
  /** Antes de recordar: qué movimientos de otros meses reconocería la regla. No cambia nada. */
  rememberPreview: (id: string, body: RememberPreviewInput) =>
    request<RememberPreview>(`/ledger/movements/${id}/remember-preview`, 'POST', { body }),
  /** Repite la identificación de los ingresos pendientes (tras dar de alta a quien faltaba). */
  retryIdentification: () =>
    request<{ identified: number; pending: number }>('/ledger/identify', 'POST'),
  deletePayerRule: (id: string) => request<void>(`/ledger/payer-rules/${id}`, 'DELETE'),
  /**
   * Lanza la conciliación con IA de lo pendiente del mes, en segundo plano. Si ya hay una en
   * curso, devuelve esa; `run: null` si no queda nada pendiente.
   */
  startAiReconciliation: (month: string) =>
    request<{ run: AiReconciliationRun | null }>(`/ledger/${month}/ai-reconcile`, 'POST'),
  aiReconciliationRun: (id: string) => request<AiReconciliationRun>(`/ledger/ai-runs/${id}`, 'GET'),

  geocode: (query: string) => request<GeocodeResponse>('/geo/search', 'GET', { params: { q: query } }),
  reverseGeocode: (lat: number, lon: number) =>
    request<GeocodeResponse>('/geo/reverse', 'GET', { params: { lat, lon } }),
  cadastral: (reference: string) =>
    request<CadastralSuggestion | null>('/geo/cadastral', 'GET', { params: { ref: reference } }),
};

/** Formas que devuelven las escrituras. Coinciden con lo que devuelve el `GET` equivalente. */
export type PropertyWriteResponse = PropertyDetailResponse & {
  organizationId: string;
  portfolioId: string;
  units: UnitWriteResponse[];
};
export type UnitWriteResponse = UnitDto & { propertyId: string; updatedAt: string };
export type TenantWriteResponse = TenantCard & {
  /** Dónde ha quedado su contrato en el calendario de la habitación, si se le dio una. */
  leasePlacement?: 'activo' | 'vencido' | 'programado' | null;
  updatedAt: string;
  /** Solo en el alta, si traía habitación: el contrato en borrador que se ha creado con ella. */
  leaseId?: string | null;
};

export interface PropertyDeedDto {
  id: string;
  kind: string;
  kindLabel: string;
  name: string;
  notary: string | null;
  protocolNumber: string | null;
  grantedAt: string | null;
  registry: string | null;
  volume: string | null;
  book: string | null;
  page: string | null;
  estateNumber: string | null;
  notes: string | null;
  createdAt: string;
  file: {
    documentId: string;
    name: string;
    mimeType: string | null;
    sizeBytes: number | null;
    url: string;
  } | null;
}

export interface AvailableUnit {
  id: string;
  code: string;
  unitType: string;
  baseRent: string;
  status: string;
  floor: string | null;
  hasPrivateBathroom: boolean;
  furnished: boolean;
  property: { id: string; name: string; city: string };
  /** Quien vive ahí ahora, con su estancia: el alta compara sus fechas con estas. */
  occupiedBy: { leaseId: string; from?: string; until: string | null; tenantName: string | null } | null;
}

/**
 * Descarga un fichero de la API.
 *
 * NO es un `<a href>`: la ruta pide `Authorization`, y el token de acceso vive en MEMORIA
 * —nunca en `localStorage`, por diseño—, así que el navegador no puede adjuntarlo solo al
 * seguir un enlace. Se pide con `fetch`, se convierte en blob y se dispara la descarga con
 * un enlace temporal.
 */
/**
 * Pide un fichero protegido y lo devuelve en memoria, con su nombre y su tipo. Lo usan la
 * descarga (que lo guarda en el disco) y el visor de documentos (que lo enseña sin guardarlo).
 */
async function fetchFile(
  path: string,
  fallbackName: string,
  errorMessage: string,
): Promise<{ blob: Blob; name: string; type: string }> {
  if (isAccessTokenExpired()) await refreshSession();
  const token = getAccessToken();
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = (await response.json()) as unknown;
    } catch {
      body = null;
    }
    const failure = (body as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      failure?.code ?? 'DOWNLOAD_FAILED',
      failure?.message ?? errorMessage,
      failure?.details,
    );
  }

  /*
   * El nombre sale de `Content-Disposition`, que el servidor manda codificado (RFC 5987)
   * justo para que un nombre con comillas o acentos no rompa la cabecera.
   */
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const name = encoded ? decodeURIComponent(encoded) : fallbackName;
  const type = (response.headers.get('Content-Type') ?? '').split(';')[0]?.trim().toLowerCase() ?? '';

  return { blob: await response.blob(), name, type };
}

async function downloadFile(path: string, fallbackName: string, errorMessage: string): Promise<void> {
  const { blob, name } = await fetchFile(path, fallbackName, errorMessage);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Sin esto el blob se queda en memoria hasta que se recarga la pestaña.
  URL.revokeObjectURL(url);
}

/**
 * Subida con PROGRESO REAL, y por eso usa `XMLHttpRequest` y no `fetch`.
 *
 * `fetch` con un `ReadableStream` de subida no sirve: Firefox no lo soporta, Safari estable
 * tampoco, y ademas mide mal - reporta lo que el navegador ha BUFFERIZADO, no lo que ha
 * salido por el cable, asi que la barra llega al 100 % con el fichero aun viajando.
 * `xhr.upload.onprogress` es la unica API que informa de bytes realmente enviados.
 */
async function uploadWithProgress<T>(
  path: string,
  file: File | null,
  onProgress?: (percent: number) => void,
  /** Campos de texto que viajan en el MISMO multipart que el fichero. */
  fields?: Record<string, string | null | undefined>,
): Promise<T> {
  if (isAccessTokenExpired()) await refreshSession();

  return new Promise<T>((resolve, reject) => {
    const form = new FormData();
    /*
     * Los campos van PRIMERO y el fichero al final. Así el servidor tiene los datos en la mano
     * antes de empezar a leer megabytes, y puede rechazar un tipo inválido sin tragarse el PDF
     * entero.
     */
    for (const [key, value] of Object.entries(fields ?? {})) {
      if (value !== null && value !== undefined && value !== '') form.append(key, value);
    }
    if (file) form.append('file', file, file.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`);
    xhr.withCredentials = true;

    const token = getAccessToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Accept', 'application/json');
    // NO se fija Content-Type: el navegador tiene que ponerlo con el boundary del multipart.

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText) as unknown;
      } catch {
        body = null;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve(body as T);
        return;
      }

      const failure = (body as ApiErrorBody | null)?.error;
      /*
       * Un 413 SIN el error de la API lo ha devuelto algo de delante —nginx, un proxy del
       * servidor— y no la API, que siempre dice su límite. «La subida ha fallado (413)» no le
       * decía a nadie que el problema era el tamaño del fichero.
       */
      const porDefecto =
        xhr.status === 413
          ? `«${file?.name ?? 'El fichero'}» es demasiado grande para subirlo.`
          : `La subida ha fallado (${xhr.status}).`;
      reject(
        new ApiError(
          xhr.status,
          failure?.code ?? 'UPLOAD_FAILED',
          failure?.message ?? porDefecto,
          failure?.details,
        ),
      );
    };

    xhr.onerror = () =>
      reject(new ApiError(0, 'NETWORK_ERROR', 'No se ha podido conectar con la API.'));
    xhr.ontimeout = () => reject(new ApiError(0, 'TIMEOUT', 'La subida ha tardado demasiado.'));
    // Generoso: 15 MB por una conexion lenta tarda.
    xhr.timeout = 120_000;

    xhr.send(form);
  });
}

/**
 * Claves de TanStack Query en un solo sitio. Centralizarlas evita el clásico
 * `['tenants']` en un fichero y `['tenant']` en otro, que rompe la invalidación en silencio
 * cuando la fase 2 añada escritura.
 */
export const queryKeys = {
  health: ['health'] as const,
  dashboardSummary: ['dashboard', 'summary'] as const,
  dashboardPerformance: ['dashboard', 'performance'] as const,
  dashboardAnalytics: ['dashboard', 'analytics'] as const,
  portfolios: ['portfolios'] as const,
  ledgerMonth: (month: string) => ['ledger', month] as const,
  aiRun: (id: string) => ['ai-run', id] as const,
  properties: (params?: unknown) => ['properties', params ?? null] as const,
  property: (id: string) => ['property', id] as const,
  tenants: (params?: unknown) => ['tenants', params ?? null] as const,
  transactions: (params?: unknown) => ['transactions', params ?? null] as const,
  rentCharges: (month?: string) => ['rent-charges', month ?? 'current'] as const,
  maintenance: (params?: unknown) => ['maintenance', params ?? null] as const,
  documents: (params?: unknown) => ['documents', params ?? null] as const,
  alerts: (params?: unknown) => ['alerts', params ?? null] as const,
  pending: () => ['pending'] as const,
  utilities: (params?: unknown) => ['utilities', params ?? null] as const,
  rotation: (year?: number) => ['rotation', year ?? null] as const,
  reports: ['reports'] as const,
  portalMe: ['portal', 'me'] as const,

  // --- Fase 2 --------------------------------------------------------------
  session: ['session'] as const,
  tenant: (id: string) => ['tenant', id] as const,
  tenantTimeline: (id: string) => ['tenant', id, 'timeline'] as const,
  tenantPurgePreview: (id: string) => ['tenant', id, 'purge-preview'] as const,
  lease: (id: string) => ['lease', id] as const,
  leaseChain: (id: string) => ['lease', id, 'chain'] as const,
  chargePreview: (id: string) => ['lease', id, 'charge-preview'] as const,
  renewalPreview: (id: string) => ['lease', id, 'renewal-preview'] as const,
  terminationPreview: (id: string, at: string) =>
    ['lease', id, 'termination-preview', at] as const,
  availableUnits: (params?: unknown) => ['units', 'available', params ?? null] as const,
  propertyDeeds: (propertyId: string) => ['properties', propertyId, 'deeds'] as const,
  propertyMap: (params?: unknown) => ['properties', 'map', params ?? null] as const,
  geocode: (query: string) => ['geocode', query] as const,
};
